import { isEmailAllowedBy } from '../lib/emailAccess';
import { isMockEmail } from '../lib/mockAuth';
import { readRuntimeConfig } from '../lib/runtimeConfig';

/**
 * Configuração do login com o Google.
 *
 * **O Client ID e a allowlist não vêm do build.** Em produção quem os entrega é
 * o monitor, em tempo de execução, pelo `/config.js` que o `index.html` carrega
 * antes do bundle (ver `packages/monitor/src/http/runtime_config.ts`): o único
 * lugar deles é o ambiente do pod. Um build sem as `VITE_*` já deixou o
 * staging aberto, e com isso a tela não depende mais delas.
 *
 * As `VITE_*` só valem no `npm run dev`, e só quando o `/config.js` não trouxe
 * nada (monitor fora do ar ou sem Client ID): `import.meta.env.DEV` vira
 * `false` no `vite build`, e o ramo que as lê some do bundle.
 *
 * A checagem daqui (allowlist) é o filtro da interface; o limite
 * de verdade é o monitor, que confere o token do Google em toda chamada à API
 * (`packages/monitor/src/http/auth.ts`).
 */
const RUNTIME = readRuntimeConfig(
  typeof window === 'undefined' ? undefined : (window as { __SICOP_CONFIG__?: unknown }).__SICOP_CONFIG__
);

/**
 * Client ID OAuth (Google Cloud Console → Credenciais → ID do cliente OAuth).
 * Vazio desabilita o botão de login — nunca abre a tela.
 */
export const GOOGLE_CLIENT_ID =
  RUNTIME.googleClientId || (import.meta.env.DEV ? (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '').trim() : '');

/**
 * Login simulado: em `npm run dev` o SICOP não fala com o Google — um SDK
 * falso (`auth/mock.ts`) deixa entrar com qualquer e-mail, inclusive
 * `@gmail.com`, que é a única conta que quem desenvolve costuma ter à mão.
 *
 * `import.meta.env.DEV` é a trava, e ela é de build: o Vite o substitui por
 * `false` literal em `vite build`, então em qualquer ambiente que não seja o
 * servidor de desenvolvimento isto é a constante `false` e o mock some do
 * bundle junto com o ramo morto. Nenhuma variável de ambiente liga o mock —
 * `VITE_AUTH_MOCK` só serve para *desligá-lo* (`false`) e testar o login real
 * do Google na máquina.
 */
export const AUTH_MOCK =
  import.meta.env.DEV && (import.meta.env.VITE_AUTH_MOCK ?? '').trim().toLowerCase() !== 'false';

/** E-mail que já vem preenchido no seletor de contas falso. */
export const AUTH_MOCK_EMAIL = import.meta.env.VITE_AUTH_MOCK_EMAIL?.trim() || 'dev@gmail.com';

/**
 * Se a tela de login aparece. **Fora do dev server, sempre.**
 *
 * O SICOP mostra dados pessoais (linhas, titulares, consumo), e o padrão
 * anterior — abrir sem login quando `VITE_REQUIRE_AUTH` não chegava ao build —
 * deixou o staging aberto: bastava o CI não passar a variável. Agora o login
 * não depende de variável nenhuma em build: `import.meta.env.DEV` é trocado
 * por `false` literal no `vite build`, e o ramo que lê `VITE_REQUIRE_AUTH` some
 * do bundle.
 *
 * No `npm run dev` o padrão também é exigir o login (o mock deixa entrar com
 * qualquer e-mail); `VITE_REQUIRE_AUTH=false` desliga, e só lá.
 */
const REQUIRE_AUTH_ENV = (import.meta.env.VITE_REQUIRE_AUTH ?? '').trim().toLowerCase();
export const REQUIRE_AUTH = import.meta.env.DEV ? REQUIRE_AUTH_ENV !== 'false' : true;

/**
 * E-mails autorizados — `ALLOWED_EMAILS` no ambiente do monitor, entregue pelo
 * `/config.js`. No dev server, sem o script, vale `VITE_ALLOWED_EMAILS`.
 *
 * Vazia, qualquer conta que o Google deixou entrar passa; preenchida, só as
 * contas listadas entram. Quais contas o Google deixa entrar é configuração do
 * Client ID no Google Cloud (tela de consentimento e usuários de teste).
 */
export const ALLOWED_EMAILS: readonly string[] =
  RUNTIME.allowedEmails ??
  (import.meta.env.DEV ? (import.meta.env.VITE_ALLOWED_EMAILS ?? '') : '')
    .split(',')
    .map((email: string) => email.trim().toLowerCase())
    .filter(Boolean);

/**
 * Texto de acesso restrito exibido abaixo do botão (vem do design).
 *
 * Com o mock ligado ele diz outra coisa, e isso não é detalhe: a tela é a
 * mesma, e sem o aviso é fácil achar que uma sessão de desenvolvimento passou
 * pelo Google.
 */
export const ACCESS_HINT_LINES = AUTH_MOCK
  ? ['Desenvolvimento: login simulado, sem passar pelo Google', 'qualquer e-mail entra']
  : ['Acesso restrito a contas autorizadas', 'via conta Google'];

/**
 * `true` quando o e-mail passa pela allowlist (quando houver uma).
 *
 * Com o mock, qualquer e-mail bem formado passa — é o objetivo dele.
 */
export function isEmailAllowed(email: string): boolean {
  if (AUTH_MOCK) return isMockEmail(email);
  return isEmailAllowedBy(email, ALLOWED_EMAILS);
}

/** Escopos mínimos para identificar quem está entrando. */
export const GOOGLE_SCOPES = 'openid email profile';

export const GOOGLE_GSI_SRC = 'https://accounts.google.com/gsi/client';
export const GOOGLE_USERINFO_URL = 'https://www.googleapis.com/oauth2/v3/userinfo';
